import { useEffect, useMemo, useState } from "react";
import { Search as SearchIcon, Pencil, RotateCcw, Save, X } from "lucide-react";
import { api } from "../../lib/api";
import { PAGE_SEO, getServiceSeo } from "../../lib/seo";
import { SERVICES } from "../../lib/services";

function buildPageList() {
  const staticPages = Object.entries(PAGE_SEO).map(([path, seo]) => ({
    path,
    defaultTitle: seo.title,
    defaultDescription: seo.description,
  }));
  const servicePages = SERVICES.map((s) => {
    const seo = getServiceSeo(s);
    return { path: `/services/${s.slug}`, defaultTitle: seo.title, defaultDescription: seo.description };
  });
  return [...staticPages, ...servicePages];
}

const ALL_PAGES = buildPageList();

export function SeoTab() {
  const [overrides, setOverrides] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editingPath, setEditingPath] = useState(null);
  const [draft, setDraft] = useState({ title: "", description: "" });
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api
      .get("/admin/seo")
      .then((res) => {
        const map = {};
        res.data.forEach((o) => {
          map[o.path] = o;
        });
        setOverrides(map);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return ALL_PAGES.filter((p) => !q || p.path.toLowerCase().includes(q)).map((p) => {
      const override = overrides[p.path];
      const hasOverride = !!(override && (override.title || override.description));
      return {
        ...p,
        effectiveTitle: hasOverride && override.title ? override.title : p.defaultTitle,
        effectiveDescription: hasOverride && override.description ? override.description : p.defaultDescription,
        hasOverride,
      };
    });
  }, [search, overrides]);

  const startEdit = (row) => {
    setEditingPath(row.path);
    setDraft({ title: row.effectiveTitle, description: row.effectiveDescription });
  };

  const save = async () => {
    if (saving || !editingPath) return;
    setSaving(true);
    try {
      await api.put("/admin/seo", { path: editingPath, title: draft.title, description: draft.description });
      setEditingPath(null);
      load();
    } catch {
      // stay in edit mode
    } finally {
      setSaving(false);
    }
  };

  const clearOverride = async (path) => {
    await api.post("/admin/seo/delete", { path }).catch(() => {});
    load();
  };

  return (
    <div data-testid="admin-seo-tab">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
        <div>
          <div className="kb-label mb-3">SEO / GEO / AEO</div>
          <h1 className="font-display text-3xl font-bold text-white">Manage Page SEO</h1>
          <p className="text-white/50 text-sm mt-2 max-w-2xl">
            Titles and descriptions default to the keyword-mapped copy built from the client's search strategy.
            Override any page here — changes apply immediately, no redeploy needed.
          </p>
        </div>
        <div className="relative w-full md:w-72">
          <SearchIcon size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search path..."
            data-testid="seo-search"
            className="w-full bg-[#0a1f4d]/60 border border-[#d4a840]/20 rounded-xl pl-11 pr-4 py-2.5 text-sm text-white placeholder-white/30 focus:border-[#d4a840] focus:outline-none transition-colors"
          />
        </div>
      </div>

      <div className="kb-glass rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-white/50 text-xs uppercase tracking-wider border-b border-white/10">
                <th className="px-5 py-4 font-medium w-56">Path</th>
                <th className="px-5 py-4 font-medium">Title</th>
                <th className="px-5 py-4 font-medium">Description</th>
                <th className="px-5 py-4 font-medium text-right w-32">Actions</th>
              </tr>
            </thead>
            <tbody>
              {!loading && rows.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-5 py-12 text-center text-white/40">No pages match.</td>
                </tr>
              ) : null}
              {rows.map((row) => {
                const isEditing = editingPath === row.path;
                return (
                  <tr key={row.path} className="border-b border-white/5 last:border-0 align-top" data-testid={`seo-row-${row.path}`}>
                    <td className="px-5 py-4">
                      <code className="text-xs text-[#d4a840]">{row.path}</code>
                      {row.hasOverride ? <div className="text-[10px] uppercase tracking-wider text-white/40 mt-1">Custom</div> : null}
                    </td>
                    {isEditing ? (
                      <>
                        <td className="px-5 py-3" colSpan={2}>
                          <input
                            value={draft.title}
                            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                            data-testid="seo-edit-title"
                            className="w-full bg-[#0a1f4d]/60 border border-[#d4a840]/20 rounded-lg px-3 py-2 text-sm text-white focus:border-[#d4a840] focus:outline-none mb-2"
                            placeholder="Page title"
                          />
                          <textarea
                            value={draft.description}
                            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                            rows={2}
                            data-testid="seo-edit-description"
                            className="w-full bg-[#0a1f4d]/60 border border-[#d4a840]/20 rounded-lg px-3 py-2 text-sm text-white focus:border-[#d4a840] focus:outline-none resize-none"
                            placeholder="Meta description"
                          />
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex items-center justify-end gap-2">
                            <button type="button" onClick={save} disabled={saving} data-testid="seo-save" className="p-2 rounded-lg border border-[#d4a840]/40 text-[#d4a840] hover:bg-[#d4a840]/10 transition-colors">
                              <Save size={14} />
                            </button>
                            <button type="button" onClick={() => setEditingPath(null)} className="p-2 rounded-lg border border-white/10 text-white/60 hover:text-white transition-colors">
                              <X size={14} />
                            </button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-5 py-4 text-white/80 max-w-xs">
                          <div className="line-clamp-2">{row.effectiveTitle}</div>
                        </td>
                        <td className="px-5 py-4 text-white/50 text-xs max-w-sm">
                          <div className="line-clamp-2">{row.effectiveDescription}</div>
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => startEdit(row)}
                              data-testid={`seo-edit-${row.path}`}
                              className="p-2 rounded-lg border border-white/10 text-white/60 hover:text-white hover:border-[#d4a840]/40 transition-colors"
                            >
                              <Pencil size={14} />
                            </button>
                            {row.hasOverride ? (
                              <button
                                type="button"
                                onClick={() => clearOverride(row.path)}
                                title="Reset to default"
                                data-testid={`seo-reset-${row.path}`}
                                className="p-2 rounded-lg border border-white/10 text-white/60 hover:text-white hover:border-[#d4a840]/40 transition-colors"
                              >
                                <RotateCcw size={14} />
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-6 kb-glass rounded-2xl p-6 text-sm text-white/60 leading-relaxed">
        <div className="kb-label mb-2">What's already handled sitewide</div>
        <ul className="space-y-1.5 list-disc list-inside">
          <li>LocalBusiness / LegalService structured data (Organization schema) on the homepage</li>
          <li>Service schema + breadcrumb structured data on every service page</li>
          <li>FAQPage structured data on the homepage FAQ section (built for Google featured snippets and AI answer engines — GEO/AEO)</li>
          <li><code className="text-[#d4a840]/80">robots.txt</code> and <code className="text-[#d4a840]/80">sitemap.xml</code></li>
          <li>Open Graph and Twitter card tags on every page</li>
          <li>"Areas We Serve" location content (Bangalore, Whitefield, Electronic City, Peenya, Yelahanka, Hosur Road, Devanahalli, Bommasandra) on the Contact page</li>
        </ul>
      </div>
    </div>
  );
}
